import React, { useState, useEffect, useMemo, useCallback } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Spinner,
    Alert,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap"
import { useNavigate } from "react-router-dom"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import axiosClient from "../../helpers/axiosClient"
import Switch from "react-switch"

const ListarUsuarios = ({ setBreadcrumbItems, user, empresaSeleccionada, empresas: reduxEmpresas }) => {
    const navigate = useNavigate()
    document.title = "Usuarios | LS-DA - Softlogy"

    const [usuarios, setUsuarios] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [searchTerm, setSearchTerm] = useState("")
    const [togglingId, setTogglingId] = useState(null)
    const [successMsg, setSuccessMsg] = useState("")
    const [filterEmpresaId, setFilterEmpresaId] = useState("")
    const [misEmpresas, setMisEmpresas] = useState([])
    const [esAdminAlianza, setEsAdminAlianza] = useState(false)

    // Modal ver empresas
    const [modalUser, setModalUser] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)

    // Modal editar usuario
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [editUser, setEditUser] = useState(null)
    const [editNombre, setEditNombre] = useState("")
    const [editApellido, setEditApellido] = useState("")
    const [editTelefono, setEditTelefono] = useState("")
    const [editEmpresas, setEditEmpresas] = useState({})
    const [editLoading, setEditLoading] = useState(false)
    const [editError, setEditError] = useState("")
    const [editSearchEmpresa, setEditSearchEmpresa] = useState("")

    // Reset password modal
    const [resetModalOpen, setResetModalOpen] = useState(false)
    const [resetTempPwd, setResetTempPwd] = useState("")
    const [resetUserName, setResetUserName] = useState("")
    const [resettingId, setResettingId] = useState(null)
    const [resetCopied, setResetCopied] = useState(false)

    // Roles modal
    const [rolesModalOpen, setRolesModalOpen] = useState(false)
    const [rolesUser, setRolesUser] = useState(null)
    const [rolesDisponibles, setRolesDisponibles] = useState([])
    const [rolesAsignados, setRolesAsignados] = useState([]) // [{rolId, empresaId}]
    const [rolesLoading, setRolesLoading] = useState(false)
    const [rolesSaving, setRolesSaving] = useState(false)
    const [rolesError, setRolesError] = useState("")

    // Breadcrumb
    useEffect(() => {
        const greeting = user
            ? `Bienvenido, ${user.nombre} ${user.apellido}`
            : "Dashboard"
        setBreadcrumbItems(greeting, [
            { title: "LS-DA", link: "#" },
            { title: "Usuarios", link: "#" },
        ])
    }, [setBreadcrumbItems, user])

    // Cargar empresas del usuario (para detectar si es admin de alianza)
    useEffect(() => {
        const loadEmpresas = async () => {
            try {
                const endpoint = user?.esSuperAdmin ? "empresas" : "empresas/mis-empresas"
                const res = await axiosClient.get(endpoint)
                const data = res.data.data || res.data
                const list = Array.isArray(data) ? data : []
                setMisEmpresas(list)
                setEsAdminAlianza(list.some(e => e.esAlianza === true))
            } catch {
                // fallback a reduxEmpresas
                if (reduxEmpresas?.length) {
                    setMisEmpresas(reduxEmpresas)
                    setEsAdminAlianza(reduxEmpresas.some(e => e.esAlianza === true))
                }
            }
        }
        loadEmpresas()
    }, [user, reduxEmpresas])

    // Fetch usuarios based on user role
    const fetchUsuarios = useCallback(async () => {
        setLoading(true)
        setError("")
        try {
            const params = new URLSearchParams()

            if (filterEmpresaId) {
                params.append("empresaId", filterEmpresaId)
            } else if (!user?.esSuperAdmin && !esAdminAlianza && empresaSeleccionada?.id) {
                // Solo filtrar por empresa si NO es superAdmin y NO es admin de alianza
                params.append("empresaId", empresaSeleccionada.id)
            }

            const queryStr = params.toString() ? `?${params.toString()}` : ""
            const response = await axiosClient.get(`usuarios${queryStr}`)
            const data = response.data.data || response.data
            setUsuarios(Array.isArray(data) ? data : [])
        } catch (err) {
            const msg = err.response?.data?.message || "Error al obtener usuarios"
            setError(typeof msg === "string" ? msg : JSON.stringify(msg))
        } finally {
            setLoading(false)
        }
    }, [filterEmpresaId, user, empresaSeleccionada, esAdminAlianza])

    useEffect(() => {
        fetchUsuarios()
    }, [fetchUsuarios])

    // Toggle active/inactive
    const handleToggleEstado = async (usuario) => {
        setTogglingId(usuario.id)
        setSuccessMsg("")
        try {
            await axiosClient.patch(`usuarios/${usuario.id}/estado`, {
                activo: !usuario.activo,
            })
            setUsuarios(prev =>
                prev.map(u =>
                    u.id === usuario.id ? { ...u, activo: !u.activo } : u
                )
            )
            setSuccessMsg(
                `${usuario.nombre} ${usuario.apellido} ${!usuario.activo ? "habilitado" : "deshabilitado"} exitosamente`
            )
            setTimeout(() => setSuccessMsg(""), 3000)
        } catch (err) {
            setError(err.response?.data?.message || "Error al cambiar estado")
        } finally {
            setTogglingId(null)
        }
    }

    // Reset password (admin)
    const handleResetPassword = async (usuario) => {
        if (!window.confirm(`¿Está seguro de resetear la contraseña de "${usuario.nombre} ${usuario.apellido}"?\n\nSe generará una contraseña temporal.`)) return
        setResettingId(usuario.id)
        setSuccessMsg("")
        setError("")
        try {
            const res = await axiosClient.put(`auth/reset-password/${usuario.id}`)
            const tempPwd = res.data?.data?.tempPassword || res.data?.tempPassword || ""
            setResetTempPwd(tempPwd)
            setResetUserName(`${usuario.nombre} ${usuario.apellido}`)
            setResetCopied(false)
            setResetModalOpen(true)
        } catch (err) {
            const msg = err.response?.data?.message || "Error al resetear la contraseña"
            setError(typeof msg === "string" ? msg : JSON.stringify(msg))
        } finally {
            setResettingId(null)
        }
    }

    const handleCopyTempPwd = () => {
        navigator.clipboard.writeText(resetTempPwd).then(() => {
            setResetCopied(true)
            setTimeout(() => setResetCopied(false), 2000)
        })
    }

    // Ver empresas modal
    const handleVerEmpresas = (usuario) => {
        setModalUser(usuario)
        setModalOpen(true)
    }

    // ── Editar usuario ──
    const handleOpenEdit = (usuario) => {
        setEditUser(usuario)
        setEditNombre(usuario.nombre || "")
        setEditApellido(usuario.apellido || "")
        setEditTelefono(usuario.telefono || "")
        setEditError("")
        setEditSearchEmpresa("")

        // Pre-seleccionar empresas actuales del usuario
        const empresasMap = {}
        if (usuario.empresas?.length) {
            usuario.empresas.forEach(eu => {
                empresasMap[eu.empresaId] = {
                    esAdminEmpresa: eu.esAdminEmpresa || false,
                    todasSucursales: eu.todasSucursales !== false,
                }
            })
        }
        setEditEmpresas(empresasMap)
        setEditModalOpen(true)
    }

    const toggleEditEmpresa = (empresaId) => {
        setEditEmpresas(prev => {
            const copy = { ...prev }
            if (copy[empresaId]) {
                delete copy[empresaId]
            } else {
                copy[empresaId] = { esAdminEmpresa: false, todasSucursales: true }
            }
            return copy
        })
    }

    const toggleEditAdmin = (empresaId) => {
        setEditEmpresas(prev => ({
            ...prev,
            [empresaId]: { ...prev[empresaId], esAdminEmpresa: !prev[empresaId]?.esAdminEmpresa }
        }))
    }

    const toggleEditSucursales = (empresaId) => {
        setEditEmpresas(prev => ({
            ...prev,
            [empresaId]: { ...prev[empresaId], todasSucursales: !prev[empresaId]?.todasSucursales }
        }))
    }

    const handleSaveEdit = async () => {
        if (!editUser) return
        setEditLoading(true)
        setEditError("")

        try {
            // 1. Update user data
            await axiosClient.put(`usuarios/${editUser.id}`, {
                nombre: editNombre,
                apellido: editApellido,
                telefono: editTelefono,
            })

            // 2. Update empresas (only for non-superadmins)
            if (!editUser.esSuperAdmin) {
                const empresasPayload = Object.entries(editEmpresas).map(([empresaId, config]) => ({
                    empresaId,
                    esAdminEmpresa: config.esAdminEmpresa,
                    todasSucursales: config.todasSucursales,
                }))
                await axiosClient.put(`usuarios/${editUser.id}/empresas`, {
                    empresas: empresasPayload,
                })
            }

            setSuccessMsg(`Usuario "${editNombre} ${editApellido}" actualizado exitosamente`)
            setTimeout(() => setSuccessMsg(""), 3000)
            setEditModalOpen(false)
            fetchUsuarios()
        } catch (err) {
            const msg = err.response?.data?.message || "Error al actualizar usuario"
            setEditError(typeof msg === "string" ? msg : JSON.stringify(msg))
        } finally {
            setEditLoading(false)
        }
    }

    // Filtered empresas for edit modal
    const filteredEditEmpresas = useMemo(() => {
        if (!reduxEmpresas) return []
        if (!editSearchEmpresa) return reduxEmpresas
        const term = editSearchEmpresa.toLowerCase()
        return reduxEmpresas.filter(e =>
            e.nombre?.toLowerCase().includes(term) || e.nit?.includes(term)
        )
    }, [reduxEmpresas, editSearchEmpresa])

    const editSelectedCount = Object.keys(editEmpresas).length

    // ── Asignar Roles ──
    const handleOpenRoles = async (usuario) => {
        setRolesUser(usuario)
        setRolesError("")
        setRolesModalOpen(true)
        setRolesLoading(true)
        try {
            const res = await axiosClient.get("roles")
            const data = res.data.data || res.data || []
            setRolesDisponibles(Array.isArray(data) ? data : [])
            // Pre-seleccionar roles actuales
            const actuales = (usuario.roles || []).map(ur => ({
                rolId: ur.rolId || ur.rol?.id,
                empresaId: ur.empresaId,
            })).filter(r => r.rolId)
            setRolesAsignados(actuales)
        } catch (err) {
            setRolesError(err.response?.data?.message || "Error al cargar roles")
        } finally {
            setRolesLoading(false)
        }
    }

    const toggleRolEmpresa = (rolId, empresaId) => {
        setRolesAsignados(prev => {
            const exists = prev.find(r => r.rolId === rolId && r.empresaId === empresaId)
            if (exists) return prev.filter(r => !(r.rolId === rolId && r.empresaId === empresaId))
            return [...prev, { rolId, empresaId }]
        })
    }

    const handleSaveRoles = async () => {
        if (!rolesUser) return
        setRolesSaving(true)
        setRolesError("")
        try {
            await axiosClient.put(`usuarios/${rolesUser.id}/roles`, {
                roles: rolesAsignados,
            })
            setSuccessMsg(`Roles asignados a "${rolesUser.nombre} ${rolesUser.apellido}"`)
            setTimeout(() => setSuccessMsg(""), 3000)
            setRolesModalOpen(false)
            fetchUsuarios()
        } catch (err) {
            const msg = err.response?.data?.message || "Error al asignar roles"
            setRolesError(typeof msg === "string" ? msg : JSON.stringify(msg))
        } finally {
            setRolesSaving(false)
        }
    }

    // Filtrar usuarios
    const filteredUsuarios = useMemo(() => {
        if (!searchTerm) return usuarios
        const term = searchTerm.toLowerCase()
        return usuarios.filter(
            u =>
                u.nombre?.toLowerCase().includes(term) ||
                u.apellido?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term) ||
                u.telefono?.includes(term)
        )
    }, [usuarios, searchTerm])

    const thStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                        <CardBody style={{ padding: "2rem" }}>
                            {/* Header */}
                            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                                <div>
                                    <h4 className="mb-1" style={{ fontWeight: 700 }}>Usuarios</h4>
                                    <small className="text-muted">
                                        {user?.esSuperAdmin
                                            ? "Todos los usuarios del sistema"
                                            : esAdminAlianza
                                                ? "Usuarios de las empresas de tu alianza"
                                                : `Usuarios de ${empresaSeleccionada?.nombre || "tu empresa"}`}
                                    </small>
                                </div>
                                <div className="d-flex gap-2 align-items-center flex-wrap">
                                    {(user?.esSuperAdmin || esAdminAlianza) && misEmpresas.length > 0 && (
                                        <select
                                            value={filterEmpresaId}
                                            onChange={e => setFilterEmpresaId(e.target.value)}
                                            style={{
                                                padding: "6px 12px", borderRadius: 8,
                                                border: "1px solid #e2e8f0", fontSize: "0.85rem",
                                                outline: "none", backgroundColor: "#f8fafc", maxWidth: 250,
                                            }}
                                        >
                                            <option value="">Todas las empresas</option>
                                            {misEmpresas.map(emp => (
                                                <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                                            ))}
                                        </select>
                                    )}
                                    <Button color="primary" onClick={() => navigate("/portal/registro")} style={{ borderRadius: 8 }}>
                                        <i className="mdi mdi-plus me-1" />Nuevo Usuario
                                    </Button>
                                </div>
                            </div>

                            {/* Messages */}
                            {successMsg && (
                                <Alert color="success" className="mb-3">
                                    <i className="mdi mdi-check-circle me-1" />{successMsg}
                                </Alert>
                            )}
                            {error && (
                                <Alert color="danger" className="mb-3">
                                    <i className="mdi mdi-alert-circle me-1" />{error}
                                    <Button color="link" size="sm" className="ms-2 p-0" onClick={fetchUsuarios}>Reintentar</Button>
                                </Alert>
                            )}

                            {/* Search */}
                            <div style={{ position: "relative", marginBottom: 20 }}>
                                <i className="mdi mdi-magnify" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 18 }} />
                                <input
                                    type="text" value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    placeholder="Buscar por nombre, email o teléfono..."
                                    style={{ width: "100%", padding: "10px 16px 10px 42px", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: "0.9rem", outline: "none", backgroundColor: "#f8fafc" }}
                                    onFocus={e => { e.target.style.borderColor = "#556ee6" }}
                                    onBlur={e => { e.target.style.borderColor = "#e2e8f0" }}
                                />
                            </div>

                            {/* Loading */}
                            {loading && (
                                <div className="text-center py-5">
                                    <Spinner color="primary" />
                                    <p className="mt-2 text-muted">Cargando usuarios...</p>
                                </div>
                            )}

                            {/* Table */}
                            {!loading && filteredUsuarios.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead>
                                            <tr style={{ backgroundColor: "#f8fafc" }}>
                                                <th style={thStyle}>Usuario</th>
                                                <th style={thStyle}>Email</th>
                                                <th style={thStyle}>Teléfono</th>
                                                <th style={thStyle}>Tipo</th>
                                                <th style={thStyle}>Empresas</th>
                                                <th style={thStyle}>Estado</th>
                                                <th style={{ ...thStyle, width: 120 }}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsuarios.map(usuario => (
                                                <tr key={usuario.id}>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: usuario.activo ? "#556ee6" : "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: "0.85rem", flexShrink: 0 }}>
                                                                {usuario.nombre?.charAt(0)?.toUpperCase()}{usuario.apellido?.charAt(0)?.toUpperCase()}
                                                            </div>
                                                            <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                                                {usuario.nombre} {usuario.apellido}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td><span style={{ fontSize: "0.85rem", color: "#475569" }}>{usuario.email}</span></td>
                                                    <td><span style={{ fontSize: "0.85rem", color: "#475569" }}>{usuario.telefono || "—"}</span></td>
                                                    <td>
                                                        {usuario.esSuperAdmin ? (
                                                            <Badge color="primary" className="px-2 py-1" style={{ fontSize: "0.75rem" }}>Super Admin</Badge>
                                                        ) : usuario.roles?.length > 0 ? (
                                                            <div className="d-flex flex-wrap gap-1">
                                                                {usuario.roles.slice(0, 2).map((ur, idx) => (
                                                                    <Badge key={idx} color="info" className="px-2 py-1" style={{ fontSize: "0.7rem" }}>
                                                                        {ur.rol?.nombre || "Rol"}
                                                                    </Badge>
                                                                ))}
                                                                {usuario.roles.length > 2 && (
                                                                    <Badge color="light" style={{ color: "#64748b", fontSize: "0.7rem" }}>+{usuario.roles.length - 2}</Badge>
                                                                )}
                                                            </div>
                                                        ) : usuario.empresas?.some(e => e.esAdminEmpresa) ? (
                                                            <Badge color="success" className="px-2 py-1" style={{ fontSize: "0.75rem" }}>Admin</Badge>
                                                        ) : (
                                                            <Badge color="secondary" className="px-2 py-1" style={{ fontSize: "0.75rem" }}>Usuario</Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {usuario.empresas?.length > 0 ? (
                                                            <Button color="link" size="sm" className="p-0" onClick={() => handleVerEmpresas(usuario)} style={{ fontSize: "0.85rem" }}>
                                                                <i className="mdi mdi-office-building-outline me-1" />
                                                                {usuario.empresas.length} empresa{usuario.empresas.length !== 1 ? "s" : ""}
                                                            </Button>
                                                        ) : (
                                                            <span className="text-muted" style={{ fontSize: "0.8rem" }}>Sin empresas</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            {togglingId === usuario.id ? (
                                                                <Spinner size="sm" color="primary" />
                                                            ) : (
                                                                <Switch
                                                                    onColor="#02a499" offColor="#dc3545"
                                                                    onChange={() => handleToggleEstado(usuario)}
                                                                    checked={usuario.activo !== false}
                                                                    height={20} width={40} handleDiameter={16}
                                                                />
                                                            )}
                                                            <small style={{ color: usuario.activo !== false ? "#059669" : "#dc3545", fontWeight: 600 }}>
                                                                {usuario.activo !== false ? "Activo" : "Inactivo"}
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Button color="outline-warning" size="sm" title="Editar"
                                                            onClick={() => handleOpenEdit(usuario)}
                                                            className="me-1" style={{ borderRadius: 6 }}>
                                                            <i className="mdi mdi-pencil-outline" />
                                                        </Button>
                                                        <Button color="outline-primary" size="sm" title="Ver empresas"
                                                            onClick={() => handleVerEmpresas(usuario)}
                                                            className="me-1" style={{ borderRadius: 6 }}>
                                                            <i className="mdi mdi-eye-outline" />
                                                        </Button>
                                                        <Button color="outline-info" size="sm" title="Resetear Contraseña"
                                                            onClick={() => handleResetPassword(usuario)}
                                                            disabled={resettingId === usuario.id}
                                                            style={{ borderRadius: 6 }}>
                                                            {resettingId === usuario.id
                                                                ? <Spinner size="sm" style={{ width: 14, height: 14 }} />
                                                                : <i className="mdi mdi-lock-reset" />}
                                                        </Button>
                                                        {!usuario.esSuperAdmin && (
                                                            <Button color="outline-secondary" size="sm" title="Asignar Roles"
                                                                onClick={() => handleOpenRoles(usuario)}
                                                                className="ms-1" style={{ borderRadius: 6 }}>
                                                                <i className="mdi mdi-shield-account-outline" />
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Empty */}
                            {!loading && filteredUsuarios.length === 0 && !error && (
                                <div className="text-center py-5">
                                    <i className="mdi mdi-account-group-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
                                    <p className="text-muted mt-2 mb-0">
                                        {searchTerm ? "No se encontraron usuarios con ese criterio" : "No hay usuarios registrados"}
                                    </p>
                                </div>
                            )}

                            {/* Count */}
                            {!loading && filteredUsuarios.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">
                                        Mostrando {filteredUsuarios.length} de {usuarios.length} usuario{usuarios.length !== 1 ? "s" : ""}
                                    </small>
                                    <Button color="outline-secondary" size="sm" onClick={fetchUsuarios} style={{ borderRadius: 6 }}>
                                        <i className="mdi mdi-refresh me-1" />Actualizar
                                    </Button>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ══════ Modal: Ver Empresas ══════ */}
            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered size="lg">
                <ModalHeader toggle={() => setModalOpen(false)}>
                    Empresas de {modalUser?.nombre} {modalUser?.apellido}
                </ModalHeader>
                <ModalBody>
                    {modalUser?.empresas?.length > 0 ? (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
                            {modalUser.empresas.map((eu, idx) => {
                                const empresa = eu.empresa || {}
                                return (
                                    <div key={idx} style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 14, backgroundColor: "#fafbfc" }}>
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            {empresa.logoUrl ? (
                                                <img src={empresa.logoUrl} alt="" style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 6 }} onError={e => { e.target.style.display = "none" }} />
                                            ) : (
                                                <div style={{ width: 32, height: 32, borderRadius: 6, backgroundColor: "#556ee6", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 14 }}>
                                                    {empresa.nombre?.charAt(0)?.toUpperCase() || "E"}
                                                </div>
                                            )}
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{empresa.nombre || "Empresa"}</div>
                                                <small className="text-muted">NIT: {empresa.nit || "—"}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex gap-3">
                                            <Badge color={eu.esAdminEmpresa ? "success" : "light"} className="px-2 py-1" style={{ fontSize: "0.7rem" }}>
                                                {eu.esAdminEmpresa ? "✓ Admin" : "No Admin"}
                                            </Badge>
                                            <Badge color={eu.todasSucursales ? "info" : "light"} className="px-2 py-1" style={{ fontSize: "0.7rem" }}>
                                                {eu.todasSucursales ? "✓ Todas Sucursales" : "Sucursales limitadas"}
                                            </Badge>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-4 text-muted">Este usuario no tiene empresas asignadas</div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" size="sm" onClick={() => setModalOpen(false)}>Cerrar</Button>
                </ModalFooter>
            </Modal>

            {/* ══════ Modal: Editar Usuario ══════ */}
            <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(false)} centered size="xl" scrollable>
                <ModalHeader toggle={() => setEditModalOpen(false)}>
                    <i className="mdi mdi-pencil me-2" />
                    Editar: {editUser?.nombre} {editUser?.apellido}
                </ModalHeader>
                <ModalBody>
                    {editError && (
                        <Alert color="danger" className="mb-3">
                            <i className="mdi mdi-alert-circle me-1" />{editError}
                        </Alert>
                    )}

                    {/* User data */}
                    <h6 style={{ fontWeight: 700, color: "#334155", marginBottom: 16 }}>
                        <i className="mdi mdi-account-outline me-1" />
                        Datos del Usuario
                    </h6>
                    <Row className="mb-4">
                        <Col md={4}>
                            <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>Nombre</label>
                            <input type="text" className="form-control" value={editNombre} onChange={e => setEditNombre(e.target.value)}
                                style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }} />
                        </Col>
                        <Col md={4}>
                            <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>Apellido</label>
                            <input type="text" className="form-control" value={editApellido} onChange={e => setEditApellido(e.target.value)}
                                style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }} />
                        </Col>
                        <Col md={4}>
                            <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>Teléfono</label>
                            <input type="text" className="form-control" value={editTelefono} onChange={e => setEditTelefono(e.target.value)}
                                style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }} />
                        </Col>
                    </Row>

                    {/* Email (readonly) */}
                    <Row className="mb-4">
                        <Col md={4}>
                            <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#94a3b8" }}>Email (no editable)</label>
                            <input type="email" className="form-control" value={editUser?.email || ""} disabled
                                style={{ borderRadius: 8, backgroundColor: "#f1f5f9", fontSize: "0.9rem" }} />
                        </Col>
                    </Row>

                    {/* Empresas (only for non-superadmin) */}
                    {!editUser?.esSuperAdmin && (
                        <>
                            <hr />
                            <h6 style={{ fontWeight: 700, color: "#334155", marginBottom: 16 }}>
                                <i className="mdi mdi-office-building-outline me-1" />
                                Empresas Asignadas
                                <Badge color="primary" className="ms-2" style={{ fontSize: "0.75rem" }}>{editSelectedCount}</Badge>
                            </h6>

                            {/* Search */}
                            <div style={{ position: "relative", marginBottom: 16 }}>
                                <i className="mdi mdi-magnify" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 18 }} />
                                <input type="text" value={editSearchEmpresa} onChange={e => setEditSearchEmpresa(e.target.value)}
                                    placeholder="Buscar empresa..."
                                    style={{ width: "100%", padding: "8px 12px 8px 36px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: "0.85rem", outline: "none", backgroundColor: "#f8fafc" }}
                                    onFocus={e => { e.target.style.borderColor = "#556ee6" }}
                                    onBlur={e => { e.target.style.borderColor = "#e2e8f0" }}
                                />
                            </div>

                            {/* Grid */}
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12, maxHeight: 400, overflowY: "auto" }}>
                                {filteredEditEmpresas.map(empresa => {
                                    const isSelected = !!editEmpresas[empresa.id]
                                    const config = editEmpresas[empresa.id] || {}
                                    return (
                                        <div key={empresa.id}
                                            style={{
                                                border: `2px solid ${isSelected ? "#556ee6" : "#e2e8f0"}`,
                                                borderRadius: 10, padding: 12,
                                                backgroundColor: isSelected ? "#f0f4ff" : "#fff",
                                                cursor: "pointer", transition: "all 0.15s",
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-2" onClick={() => toggleEditEmpresa(empresa.id)}>
                                                <input type="checkbox" checked={isSelected} onChange={() => toggleEditEmpresa(empresa.id)}
                                                    style={{ width: 16, height: 16, accentColor: "#556ee6", cursor: "pointer" }} />
                                                {empresa.logoUrl ? (
                                                    <img src={empresa.logoUrl} alt="" style={{ width: 28, height: 28, objectFit: "contain", borderRadius: 4 }}
                                                        onError={e => { e.target.style.display = "none" }} />
                                                ) : (
                                                    <div style={{ width: 28, height: 28, borderRadius: 4, backgroundColor: isSelected ? "#556ee6" : "#94a3b8", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 12 }}>
                                                        {empresa.nombre?.charAt(0)?.toUpperCase()}
                                                    </div>
                                                )}
                                                <div style={{ overflow: "hidden", flex: 1 }}>
                                                    <div style={{ fontWeight: 600, fontSize: "0.82rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {empresa.nombre}
                                                    </div>
                                                    <small style={{ color: "#94a3b8", fontSize: "0.7rem" }}>NIT: {empresa.nit}</small>
                                                </div>
                                            </div>

                                            {isSelected && (
                                                <div className="d-flex gap-3 mt-2 pt-2" style={{ borderTop: "1px solid #e2e8f0" }}>
                                                    <div className="d-flex align-items-center gap-1">
                                                        <Switch onColor="#556ee6" onChange={() => toggleEditAdmin(empresa.id)}
                                                            checked={config.esAdminEmpresa || false} height={16} width={32} handleDiameter={12} />
                                                        <small style={{ fontSize: "0.75rem", color: "#475569" }}>Admin</small>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-1">
                                                        <Switch onColor="#02a499" onChange={() => toggleEditSucursales(empresa.id)}
                                                            checked={config.todasSucursales !== false} height={16} width={32} handleDiameter={12} />
                                                        <small style={{ fontSize: "0.75rem", color: "#475569" }}>Todas Suc.</small>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    )}

                    {editUser?.esSuperAdmin && (
                        <Alert color="info" className="mt-3 mb-0">
                            <i className="mdi mdi-information-outline me-1" />
                            Este usuario es Super Administrador y tiene acceso a todas las empresas.
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" size="sm" onClick={() => setEditModalOpen(false)} disabled={editLoading}>
                        Cancelar
                    </Button>
                    <Button color="primary" size="sm" onClick={handleSaveEdit} disabled={editLoading} style={{ minWidth: 140, borderRadius: 8 }}>
                        {editLoading ? (
                            <><Spinner size="sm" className="me-1" />Guardando...</>
                        ) : (
                            <><i className="mdi mdi-content-save me-1" />Guardar Cambios</>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ══════ Modal: Contraseña Temporal ══════ */}
            <Modal isOpen={resetModalOpen} toggle={() => setResetModalOpen(false)} centered>
                <ModalHeader toggle={() => setResetModalOpen(false)}>
                    <i className="mdi mdi-lock-reset me-2" style={{ color: "#17a2b8" }} />
                    Contraseña Reseteada
                </ModalHeader>
                <ModalBody>
                    <Alert color="success" className="mb-3">
                        <i className="mdi mdi-check-circle me-1" />
                        La contraseña de <strong>{resetUserName}</strong> ha sido reseteada exitosamente.
                    </Alert>

                    <div style={{ textAlign: "center", marginBottom: 16 }}>
                        <small className="text-muted d-block mb-2">Contraseña temporal generada:</small>
                        <div style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 12,
                            background: "#f1f5f9",
                            borderRadius: 10,
                            padding: "12px 20px",
                            border: "2px dashed #cbd5e1",
                        }}>
                            <code style={{ fontSize: "1.3rem", fontWeight: 700, letterSpacing: 2, color: "#1e293b" }}>
                                {resetTempPwd}
                            </code>
                            <Button color={resetCopied ? "success" : "primary"} size="sm" onClick={handleCopyTempPwd} style={{ borderRadius: 6 }}>
                                <i className={`mdi ${resetCopied ? "mdi-check" : "mdi-content-copy"} me-1`} />
                                {resetCopied ? "Copiada" : "Copiar"}
                            </Button>
                        </div>
                    </div>

                    <Alert color="warning" className="mb-0" style={{ fontSize: "0.85rem" }}>
                        <i className="mdi mdi-information-outline me-1" />
                        Comunique esta contraseña temporal al usuario. Al iniciar sesión, deberá cambiarla por una nueva.
                    </Alert>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setResetModalOpen(false)} style={{ borderRadius: 8 }}>
                        Cerrar
                    </Button>
                </ModalFooter>
            </Modal>

            {/* ══════ Modal: Asignar Roles ══════ */}
            <Modal isOpen={rolesModalOpen} toggle={() => setRolesModalOpen(false)} centered size="lg" scrollable>
                <ModalHeader toggle={() => setRolesModalOpen(false)}>
                    <i className="mdi mdi-shield-account-outline me-2" style={{ color: "#556ee6" }} />
                    Roles de {rolesUser?.nombre} {rolesUser?.apellido}
                </ModalHeader>
                <ModalBody>
                    {rolesError && (
                        <Alert color="danger" className="mb-3">
                            <i className="mdi mdi-alert-circle me-1" />{rolesError}
                        </Alert>
                    )}

                    <Alert color="warning" className="mb-3" style={{ fontSize: "0.85rem" }}>
                        <i className="mdi mdi-information-outline me-1" />
                        <strong>Atención:</strong> Al guardar los roles, la sesión del usuario será invalidada y deberá volver a iniciar sesión.
                    </Alert>

                    {rolesLoading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <p className="mt-2 text-muted">Cargando roles...</p>
                        </div>
                    ) : (
                        <div>
                            <small className="text-muted d-block mb-3">
                                Seleccione los roles para cada empresa. Un usuario puede tener diferentes roles en diferentes empresas.
                            </small>
                            {rolesDisponibles.filter(r => !r.esSistema || r.nombre !== "Super Admin").map(rol => {
                                const empresasParaRol = rol.empresaId
                                    ? (reduxEmpresas || []).filter(e => e.id === rol.empresaId)
                                    : (reduxEmpresas || [])

                                return (
                                    <div key={rol.id} style={{ marginBottom: 12, border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                                        <div style={{ padding: "10px 14px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                            <div className="d-flex align-items-center gap-2">
                                                <span style={{ fontWeight: 600, fontSize: "0.9rem", color: "#1e293b" }}>{rol.nombre}</span>
                                                {rol.esSistema && (
                                                    <Badge color="warning" style={{ fontSize: "0.65rem" }}>Sistema</Badge>
                                                )}
                                                {!rol.empresaId && (
                                                    <Badge color="light" style={{ color: "#64748b", fontSize: "0.65rem" }}>Global</Badge>
                                                )}
                                            </div>
                                            {rol.descripcion && (
                                                <small style={{ color: "#94a3b8", fontSize: "0.78rem" }}>{rol.descripcion}</small>
                                            )}
                                        </div>
                                        <div style={{ padding: "6px 14px", maxHeight: 200, overflowY: "auto" }}>
                                            {empresasParaRol.length > 0 ? empresasParaRol.map(emp => {
                                                const isChecked = rolesAsignados.some(r => r.rolId === rol.id && r.empresaId === emp.id)
                                                return (
                                                    <div key={emp.id}
                                                        onClick={() => toggleRolEmpresa(rol.id, emp.id)}
                                                        style={{
                                                            display: "flex", alignItems: "center", gap: 8, padding: "6px 0",
                                                            cursor: "pointer", borderBottom: "1px solid #f1f5f9",
                                                        }}>
                                                        <input type="checkbox" checked={isChecked}
                                                            onChange={() => toggleRolEmpresa(rol.id, emp.id)}
                                                            style={{ width: 14, height: 14, accentColor: "#556ee6", cursor: "pointer" }} />
                                                        <span style={{ fontSize: "0.85rem", color: "#475569" }}>{emp.nombre}</span>
                                                        <small style={{ color: "#94a3b8", fontSize: "0.72rem", marginLeft: "auto" }}>NIT: {emp.nit}</small>
                                                    </div>
                                                )
                                            }) : (
                                                <small className="text-muted">No hay empresas disponibles para este rol</small>
                                            )}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" size="sm" onClick={() => setRolesModalOpen(false)} disabled={rolesSaving}>Cancelar</Button>
                    <Button color="primary" size="sm" onClick={handleSaveRoles} disabled={rolesSaving || rolesLoading} style={{ minWidth: 140, borderRadius: 8 }}>
                        {rolesSaving ? (<><Spinner size="sm" className="me-1" />Guardando...</>) : (<><i className="mdi mdi-content-save me-1" />Guardar Roles</>)}
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    user: state.Login.user,
    empresaSeleccionada: state.Login.empresaSeleccionada,
    empresas: state.Login.empresas,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(ListarUsuarios)
